import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Chip, CircularProgress, TextField, InputAdornment, IconButton,
  Tooltip, LinearProgress, Alert, Tabs, Tab, Divider,
} from "@mui/material";
import AccountStatusTable from "./AccountStatusTable";
import PlatformActiveUsers from "./PlatformActiveUsers";
import ProductAnalytics from "./analytics/ProductAnalytics";
import { isPlatformAdmin } from "./utils/isPlatformAdmin";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsightsIcon from "@mui/icons-material/Insights";
import PaidIcon from "@mui/icons-material/Paid";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import HubIcon from "@mui/icons-material/HubOutlined";
import AnalyticsIcon from "@mui/icons-material/AnalyticsOutlined";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import { getAllTenants, getTenantsLastLogin, getTenantUserDirectory } from "./api/tenantApi";
import CreateTenantModal from "./CreateTenantModal";
import InviteUsersModal from "./InviteUsersModal";
import TenantAdminActions from "./components/TenantAdminActions";
import DistributorsManager from "./DistributorsManager";
import { recordTenantVisit, getTenantVisits } from "./utils/recentTenants";
import { tenantMatchesQuery } from "./utils/tenantSearch";
import { deriveTenantUsage } from "./utils/tenantUsage";
import InternalDocumentsFolder from "./InternalDocumentsFolder";
import { canAccessInternalDocuments } from "./utils/internalDocumentsAccess";

const INK = "#0B2A4A", BLUE = "#1E6FE8", GREEN = "#12A150", MUTED = "#64748b", LINE = "#e3ebf3";

const fmt = (n) => (n == null ? "0" : Number(n).toLocaleString());
const fmtDate = (v) => {
  if (!v) return "—";
  try { const d = new Date(v); return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};
// Relative "last seen" — e.g. "just now", "3h ago", "2d ago", "Mar 4".
const fmtRelative = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const KpiCard = ({ label, value, sub, icon, tone = BLUE }) => (
  <Box data-track-area={`${label} summary`} sx={{
    border: `1px solid ${LINE}`, borderRadius: "16px", p: { xs: 1.75, md: 2 }, background: "#fff",
    minWidth: 0, boxShadow: "0 1px 2px rgba(11,42,74,.035)",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
      <Box sx={{ width: 36, height: 36, flex: "0 0 auto", borderRadius: "10px", color: tone,
        background: `${tone}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".065em", color: MUTED }}>{label}</Typography>
    </Box>
    <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 800, color: INK, lineHeight: 1.05, letterSpacing: "-.025em" }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.55, lineHeight: 1.35 }}>{sub}</Typography>}
  </Box>
);

const WORKSPACES = [
  { value: "tenants", label: "Tenants", description: "Accounts and access", icon: <ApartmentIcon /> },
  { value: "subs", label: "Subscriptions", description: "Plans and renewals", icon: <PaidIcon /> },
  { value: "distributors", label: "Distributors", description: "Partners and clients", icon: <HubIcon /> },
  { value: "agreements", label: "Agreement templates", description: "Private legal library", icon: <FolderSpecialOutlinedIcon /> },
  { value: "users", label: "Active users", description: "Usage and journeys", icon: <BadgeIcon /> },
  { value: "analytics", label: "Product analytics", description: "Behavior and adoption", icon: <AnalyticsIcon /> },
];

const WORKSPACE_COPY = {
  tenants: { title: "Tenant directory", description: "Find an account, review its setup, or open its admin tools." },
  subs: { title: "Subscriptions", description: "Review plan status, renewals, limits, and accounts that need attention." },
  distributors: { title: "Distributors", description: "Manage brokers, carriers, access, and assigned client accounts." },
  agreements: { title: "Agreement templates", description: "Private library of reusable client and partner agreements." },
  users: { title: "Active users", description: "See who is using the platform and inspect individual journeys." },
  analytics: { title: "Product analytics", description: "Understand adoption, behavior, and conversion across the platform." },
};

export default function PlatformConsole() {
  const isAdmin = useSelector((s) => s.userInfo.isAdmin);
  const email = useSelector((s) => s.userInfo.email);
  const platformAdmin = isPlatformAdmin(email, isAdmin);
  const internalDocumentsAccess = canAccessInternalDocuments(email, isAdmin);
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mode, setMode] = useState("tenants");

  const applyTenantUpdate = (updated) =>
    setTenants((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));

  const [createOpen, setCreateOpen] = useState(false);
  const [inviteTenant, setInviteTenant] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTenant, setMenuTenant] = useState(null);
  const [lastActivity, setLastActivity] = useState({});
  const [userDirectory, setUserDirectory] = useState({});
  // tenantId -> when THIS admin last opened it, so the tenants they enter float to the top.
  const [tenantVisits, setTenantVisits] = useState(() => getTenantVisits());

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [res, directoryRes] = await Promise.all([
        getAllTenants(),
        getTenantUserDirectory().catch(() => ({ data: {} })),
      ]);
      const list = res?.data || res?.tenants || [];
      setTenants(Array.isArray(list) ? list : []);
      setUserDirectory(directoryRes?.data || {});
      // Fetch last in-product activity per tenant in the background (don't block the table).
      getTenantsLastLogin()
        .then((r) => setLastActivity(r?.data || {}))
        .catch(() => setLastActivity({}));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load tenants");
      setTenants([]);
      setUserDirectory({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (platformAdmin) load(); }, [platformAdmin, load]);

  const kpis = useMemo(() => {
    const t = tenants || [];
    return {
      total: t.length,
      paid: t.filter((x) => x.isPaid).length,
      users: t.reduce((s, x) => s + (x.emailList?.length || 0), 0),
      counterparties: t.reduce((s, x) => s + (Number(x.counter) || 0), 0),
      capacity: t.reduce((s, x) => s + (Number(x.maxCounterLimit) || 0), 0),
    };
  }, [tenants]);

  const view = useMemo(() => {
    const list = tenants.filter((tenant) => {
      if (statusFilter === "paid" && !tenant.isPaid) return false;
      if (statusFilter === "trial" && tenant.isPaid) return false;
      return tenantMatchesQuery(tenant, query, userDirectory);
    });
    // Tenants this admin has opened come first (most-recently entered on top); the rest stay alphabetical.
    return [...list].sort((a, b) => {
      const va = tenantVisits[a.id] || 0, vb = tenantVisits[b.id] || 0;
      if (va !== vb) return vb - va;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [tenants, query, statusFilter, tenantVisits, userDirectory]);

  // Record that the admin entered a tenant (so it floats to the top next time — and live now).
  const enterTenant = (t, path) => {
    if (t?.id) {
      recordTenantVisit(t.id);
      setTenantVisits((prev) => ({ ...prev, [t.id]: Date.now() }));
    }
    navigate(path);
  };

  const openMenu = (e, t) => { setMenuAnchor(e.currentTarget); setMenuTenant(t); };
  // Keep the selected tenant after the row menu closes. TenantAdminActions owns dialogs and
  // background-action logs that must outlive the menu itself; clearing the tenant here unmounted
  // those dialogs before they could open or receive the completed API response.
  const closeMenu = () => { setMenuAnchor(null); };

  if (!platformAdmin) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", px: 4, py: 8, textAlign: "center" }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: INK, mb: 1 }}>Platform Console</Typography>
        <Typography sx={{ fontSize: 14.5, color: MUTED }}>This section is available to CrediArc platform administrators only.</Typography>
      </Box>
    );
  }

  const GRID = "minmax(220px,2fr) minmax(125px,1fr) 70px minmax(130px,1.15fr) minmax(95px,.8fr) minmax(105px,.9fr) minmax(90px,.8fr) 84px";
  const activeWorkspace = WORKSPACE_COPY[mode];
  const visibleWorkspaces = WORKSPACES.filter((workspace) => workspace.value !== "agreements" || internalDocumentsAccess);
  const filtersActive = Boolean(query.trim()) || statusFilter !== "all";

  return (
    <Box data-track-area="Platform Console page content" sx={{ background: "#f5f7fa", minHeight: "100%", py: { xs: 2, md: 3.5 } }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3.5 } }}>
        <Box component="header" data-track-area="Platform Console header" sx={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2,
          flexWrap: "wrap", mb: 3,
        }}>
          <Box sx={{ maxWidth: 720 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: BLUE, textTransform: "uppercase", letterSpacing: ".11em", mb: .75 }}>
              Internal administration
            </Typography>
            <Typography component="h1" sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 800, color: INK, letterSpacing: "-.035em", lineHeight: 1.1 }}>
              Platform Console
            </Typography>
            <Typography sx={{ fontSize: 14.5, lineHeight: 1.55, color: MUTED, mt: .75 }}>
              Manage accounts, access, subscriptions, partners, and product usage from one place.
            </Typography>
          </Box>
          <Box data-track-area="Platform Console header actions" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button onClick={load} disabled={loading} variant="outlined" startIcon={loading ? <CircularProgress size={15} /> : <RefreshIcon />}
              aria-label="Refresh platform data" sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", color: INK, borderColor: "#cbd7e5", background: "#fff" }}>
              Refresh
            </Button>
            <Button onClick={() => setCreateOpen(true)} variant="contained" startIcon={<AddBusinessIcon />}
              sx={{ textTransform: "none", fontWeight: 750, borderRadius: "10px", background: BLUE, boxShadow: "0 4px 10px rgba(30,111,232,.2)" }}>
              New tenant
            </Button>
          </Box>
        </Box>

        <Box component="section" aria-labelledby="platform-overview-heading" data-track-area="Platform overview" sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.25 }}>
            <Typography id="platform-overview-heading" component="h2" sx={{ fontSize: 14, fontWeight: 800, color: INK }}>Platform overview</Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>Live account totals</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", md: "repeat(5,minmax(0,1fr))" }, gap: 1.25 }}>
            <KpiCard label="Tenants" value={loading ? "—" : fmt(kpis.total)} sub={`${kpis.paid} paid · ${kpis.total - kpis.paid} trial`} icon={<ApartmentIcon sx={{ fontSize: 19 }} />} />
            <KpiCard label="Team members" value={loading ? "—" : fmt(kpis.users)} sub="Across all accounts" tone="#7557D9" icon={<GroupIcon sx={{ fontSize: 19 }} />} />
            <KpiCard label="Counterparties" value={loading ? "—" : fmt(kpis.counterparties)} sub="Underwritten to date" tone={GREEN} icon={<InsightsIcon sx={{ fontSize: 19 }} />} />
            <KpiCard label="Capacity" value={loading ? "—" : fmt(kpis.capacity)} sub="Total configured limit" tone="#C77B13" icon={<PaidIcon sx={{ fontSize: 19 }} />} />
            <Box sx={{ gridColumn: { xs: "span 2", md: "auto" } }}>
              <KpiCard label="Paid share" value={loading ? "—" : `${kpis.total ? Math.round((kpis.paid / kpis.total) * 100) : 0}%`} sub="Paid accounts vs trials" tone={GREEN} icon={<PaidIcon sx={{ fontSize: 19 }} />} />
            </Box>
          </Box>
        </Box>

        <Box component="nav" aria-label="Platform Console work areas" data-track-area="Platform Console navigation" sx={{
          background: "#fff", border: `1px solid ${LINE}`, borderRadius: "16px", px: .75, mb: 2,
          boxShadow: "0 1px 2px rgba(11,42,74,.03)", overflow: "hidden",
        }}>
          <Tabs value={mode} onChange={(_, v) => setMode(v)} variant="scrollable" scrollButtons="auto"
            sx={{ minHeight: 68, "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0", background: BLUE },
              "& .MuiTab-root": { textTransform: "none", minHeight: 68, minWidth: 160, px: 1.5, color: MUTED, alignItems: "flex-start" },
              "& .Mui-selected": { color: `${INK} !important`, background: "#f8fbff" } }}>
            {visibleWorkspaces.map((item) => (
              <Tab key={item.value} value={item.value} aria-label={`${item.label}: ${item.description}`} label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, textAlign: "left" }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 750, lineHeight: 1.25 }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: "#8a9aab", lineHeight: 1.25, mt: .2 }}>{item.description}</Typography>
                  </Box>
                </Box>
              } />
            ))}
          </Tabs>
        </Box>

        <Box component="main" data-track-area={`${activeWorkspace.title} workspace`} sx={{
          background: "#fff", border: `1px solid ${LINE}`, borderRadius: "18px", overflow: "hidden",
          boxShadow: "0 2px 7px rgba(11,42,74,.04)",
        }}>
          {mode !== "analytics" && (
            <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2.25 }}>
              <Typography component="h2" sx={{ fontSize: 20, fontWeight: 800, color: INK, letterSpacing: "-.015em" }}>{activeWorkspace.title}</Typography>
              <Typography sx={{ fontSize: 13.5, color: MUTED, mt: .35 }}>{activeWorkspace.description}</Typography>
            </Box>
          )}

          {mode !== "analytics" && <Divider />}

          {mode !== "analytics" && loading && (
            <Box role="status" sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 10, gap: 1.5 }}>
              <CircularProgress size={27} /><Typography sx={{ color: MUTED, fontSize: 13.5 }}>Loading platform data…</Typography>
            </Box>
          )}
          {!loading && error && (
            <Alert severity="warning" sx={{ m: 2.5, borderRadius: "12px" }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
              {error}
            </Alert>
          )}

          {!loading && !error && mode === "subs" && <Box sx={{ p: { xs: 2, md: 2.5 } }}><AccountStatusTable tenants={tenants} onUpdated={applyTenantUpdate} /></Box>}
          {!loading && !error && mode === "distributors" && <Box sx={{ p: { xs: 2, md: 2.5 } }}><DistributorsManager tenants={tenants} /></Box>}
          {!loading && !error && mode === "agreements" && internalDocumentsAccess && <InternalDocumentsFolder />}
          {!loading && mode === "users" && <Box sx={{ p: { xs: 2, md: 2.5 } }}><PlatformActiveUsers /></Box>}
          {mode === "analytics" && <ProductAnalytics embedded />}

          {!loading && !error && mode === "tenants" && (
            <>
              <Box data-track-area="Tenant directory controls" sx={{
                px: { xs: 2, md: 2.5 }, py: 1.75, display: "flex", alignItems: "center",
                justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", background: "#fbfcfe",
              }}>
                <Box role="group" aria-label="Filter tenants by subscription status" sx={{ display: "flex", gap: .5 }}>
                  {[{ value: "all", label: "All" }, { value: "paid", label: "Paid" }, { value: "trial", label: "Trial" }].map((item) => (
                    <Button key={item.value} size="small" onClick={() => setStatusFilter(item.value)}
                      aria-pressed={statusFilter === item.value} variant={statusFilter === item.value ? "contained" : "text"}
                      sx={{ minWidth: 58, textTransform: "none", fontSize: 12.5, fontWeight: 700, borderRadius: "8px",
                        color: statusFilter === item.value ? "#fff" : MUTED, boxShadow: "none", background: statusFilter === item.value ? INK : "transparent",
                        "&:hover": { background: statusFilter === item.value ? INK : "#eef3f8", boxShadow: "none" } }}>
                      {item.label}
                    </Button>
                  ))}
                </Box>
                <TextField value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tenant, user, or email" size="small"
                  inputProps={{ "aria-label": "Search tenants by tenant name, user name, or email" }}
                  sx={{ width: { xs: "100%", sm: 340 }, "& .MuiOutlinedInput-root": { borderRadius: "10px", background: "#fff" } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>,
                    endAdornment: query ? <InputAdornment position="end"><IconButton size="small" onClick={() => setQuery("")} aria-label="Clear tenant search"><CloseIcon sx={{ fontSize: 17 }} /></IconButton></InputAdornment> : null,
                  }} />
              </Box>

              <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.15, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${LINE}` }}>
                <Typography role="status" sx={{ fontSize: 12.5, color: MUTED }}>
                  Showing <strong style={{ color: INK }}>{view.length}</strong> of {tenants.length} tenants
                </Typography>
                <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: 11.5, color: "#8a9aab" }}>Recently opened accounts appear first</Typography>
              </Box>

              <Box data-track-area="Tenant directory table" sx={{ overflowX: "auto", borderTop: `1px solid ${LINE}` }}>
                <Box sx={{ minWidth: 1080 }}>
                  <Box role="row" sx={{ display: "grid", gridTemplateColumns: GRID, gap: 1, alignItems: "center", background: "#f7f9fc", px: 2.5, py: 1.15 }}>
                    {["Tenant", "Status", "Team", "Usage", "AI engine", "Created", "Last seen", "Actions"].map((h) => (
                      <Typography role="columnheader" key={h} sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".055em", color: "#7f90a3" }}>{h}</Typography>
                    ))}
                  </Box>
                  <Box sx={{ maxHeight: "58vh", overflowY: "auto" }}>
                    {view.length === 0 && (
                      <Box sx={{ textAlign: "center", py: 7, px: 2 }}>
                        <SearchIcon sx={{ color: "#b5c1ce", fontSize: 30, mb: 1 }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: INK }}>No matching tenants</Typography>
                        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: .5 }}>Try another search or reset the current filters.</Typography>
                        {filtersActive && <Button size="small" onClick={() => { setQuery(""); setStatusFilter("all"); }} sx={{ textTransform: "none", mt: 1.25, fontWeight: 700 }}>Clear filters</Button>}
                      </Box>
                    )}
                    {view.map((t) => {
                      const { used, limit: cap, pct } = deriveTenantUsage(t.counter, t.maxCounterLimit);
                      return (
                        <Box role="row" key={t.id} data-track-area={`${t.name || "Tenant"} tenant row`} sx={{
                          display: "grid", gridTemplateColumns: GRID, gap: 1, alignItems: "center", px: 2.5, py: 1.35,
                          borderTop: `1px solid #edf2f7`, transition: "background .15s",
                          "&:hover": { background: "#f8fbff" }, "&:focus-within": { background: "#f5f9ff" },
                        }}>
                          <Box role="cell" sx={{ minWidth: 0 }}>
                            <Button onClick={() => enterTenant(t, `/platform/tenant/${t.id}`)} data-track-label={`Open ${t.name || "tenant"} details`}
                              sx={{ display: "block", minWidth: 0, maxWidth: "100%", p: 0, textTransform: "none", textAlign: "left", fontSize: 14,
                                fontWeight: 750, color: BLUE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.4,
                                "&:hover": { background: "transparent", textDecoration: "underline" } }}>
                              {t.name || "Unnamed tenant"}
                            </Button>
                            <Typography title={t.email || t.id} sx={{ fontSize: 11.5, color: "#8fa0b2", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: .2 }}>{t.email || t.id}</Typography>
                          </Box>
                          <Box role="cell" sx={{ display: "flex", gap: .5, flexWrap: "wrap" }}>
                            <Chip size="small" label={t.isPaid ? "Paid" : "Trial"} sx={{ height: 21, fontSize: 10.5, fontWeight: 750, color: t.isPaid ? "#16703a" : "#8b5c08", background: t.isPaid ? "#e4f7ea" : "#fff2cc" }} />
                            {t.isAREnabled && <Chip size="small" label="AR" sx={{ height: 21, fontSize: 10, background: "#eef0ff", color: "#4b4f9f" }} />}
                            {t.tenantType === "payment_platform" && <Chip size="small" label="MCA / loans" sx={{ height: 21, fontSize: 10, background: "#e5f5fc", color: "#075985", fontWeight: 700 }} />}
                          </Box>
                          <Typography role="cell" sx={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>{t.emailList?.length || 0}</Typography>
                          <Box role="cell" sx={{ pr: 1 }}>
                            <Typography sx={{ fontSize: 12, color: "#334155", mb: .45 }}>{fmt(used)} / {cap ? fmt(cap) : "∞"}</Typography>
                            {cap ? <LinearProgress variant="determinate" value={pct} aria-label={`${pct}% of tenant capacity used`} sx={{ height: 5, borderRadius: 3, backgroundColor: "#e8eef5", "& .MuiLinearProgress-bar": { borderRadius: 3, backgroundColor: pct > 85 ? "#D88A1C" : BLUE } }} /> : null}
                          </Box>
                          <Typography role="cell" sx={{ fontSize: 12.5, color: MUTED, textTransform: "capitalize" }}>{t.aiEngine || "Default"}</Typography>
                          <Typography role="cell" sx={{ fontSize: 12.5, color: MUTED }}>{fmtDate(t.metadata?.createdAt)}</Typography>
                          <Tooltip title={lastActivity[t.id] ? new Date(lastActivity[t.id]).toLocaleString() : "No product activity recorded"} arrow>
                            <Typography role="cell" sx={{ fontSize: 12.5, color: lastActivity[t.id] ? "#334155" : "#aebac7", fontWeight: lastActivity[t.id] ? 650 : 400 }}>{fmtRelative(lastActivity[t.id])}</Typography>
                          </Tooltip>
                          <Box role="cell" sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: .25 }}>
                            <Tooltip title={`Open ${t.name || "tenant"}`} arrow>
                              <IconButton size="small" onClick={() => enterTenant(t, `/platform/tenant/${t.id}`)} aria-label={`Open ${t.name || "tenant"} details`}><OpenInNewIcon sx={{ fontSize: 18 }} /></IconButton>
                            </Tooltip>
                            <Tooltip title="More tenant actions" arrow>
                              <IconButton size="small" onClick={(e) => openMenu(e, t)} aria-label={`More actions for ${t.name || "tenant"}`}><MoreVertIcon sx={{ fontSize: 19 }} /></IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* row actions — full per-tenant admin capability set, shared with the tenant page */}
      <TenantAdminActions
        variant="menu"
        tenant={menuTenant}
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeMenu}
        onChanged={(updated) => setTenants((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
      />

      <CreateTenantModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => { load(); if (created?.id) setInviteTenant(created); }}
      />
      <InviteUsersModal open={!!inviteTenant} tenant={inviteTenant} onClose={() => { setInviteTenant(null); load(); }} />
    </Box>
  );
}
